import PropTypes from 'prop-types'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import Icon from 'components/core/Icon/Icon'
import useIntersectionObserver from 'components/core/Intersection/hooks/useIntersectionObserver'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import classNames from 'lib/util/classNames'
import { useSlideshowContext } from '../Slideshow'
import Indicator from './Indicator/Indicator'
import styles from './TimedIndicators.module.scss'

/**
 * The slideshow `TimedIndicators`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function TimedIndicators (props) {
  const { className } = props
  const {
    groupCount,
    hasOverflowRight,
    isInteracting,
    numSlides,
    onScrollToSlide,
    slideIndex
  } = useSlideshowContext()
  const [isPaused, setIsPaused] = useState(false)
  const numIndicators = useMemo(
    () => Math.ceil(numSlides / groupCount),
    [numSlides, groupCount]
  )
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.container,
        numIndicators <= 1 && styles.none
      ]),
    [className, numIndicators]
  )
  const ref = useRef()
  const { isIntersecting } = useIntersectionObserver(ref)
  const items = useMemo(
    () => Array.from({ length: numIndicators }),
    [numIndicators]
  )

  const handleClickToggle = useCallback(() => {
    setIsPaused(wasPaused => !wasPaused)
  }, [])
  const toggleIcon = useMemo(() => (!isPaused ? 'pause' : 'play'), [isPaused])

  return (
    <div ref={ref} className={classNameOutput}>
      <div className={styles.playback}>
        <Tooltip.Target
          Tag="button"
          className={styles.toggle}
          tooltipContent={
            <Tooltip size="small">
              <Tooltip.Header>Toggle Autoplay</Tooltip.Header>
            </Tooltip>
          }
          onClick={handleClickToggle}
        >
          <Icon className={styles.icon} icon={toggleIcon} />
        </Tooltip.Target>
      </div>
      <div className={styles.indicators}>
        {items.map((_, index) => {
          const groupIndex = !hasOverflowRight
            ? numIndicators - 1
            : Math.round(slideIndex / groupCount)

          return (
            <Indicator
              key={index}
              isActive={groupIndex === index}
              isComplete={groupIndex > index}
              onClick={() => {
                onScrollToSlide(index * groupCount)
              }}
              onAutoplayComplete={() => {
                onScrollToSlide(index * groupCount + groupCount)
              }}
              index={index}
              currentIndex={groupIndex}
              isPaused={isPaused || isInteracting || !isIntersecting}
            />
          )
        })}
      </div>
    </div>
  )
}

/** @type {object} */
TimedIndicators.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
TimedIndicators.defaultProps = {
  className: ''
}

// Memoize
export default memo(TimedIndicators)
